// Campaign Portfolio — ESS program view (Amendment 1)
// Migrated to Cyan Room design system

import { pbAuthedFetch } from '../authHeaders';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTableControls, type Column } from '../hooks/useTableControls';
import { DataTable } from '../components/DataTable';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

export function CampaignsPage() {
  const { data: campaignsData, isLoading, error, refetch } = useQuery({
    queryKey: ['campaigns'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/campaigns');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const campaigns = campaignsData?.campaigns || [];

  const { data: detailData } = useQuery({
    queryKey: ['campaign-detail', selectedId],
    queryFn: async () => {
      const res = await pbAuthedFetch(`/api/campaigns/${selectedId}`);
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    enabled: !!selectedId,
  });

  const { data: burnupData } = useQuery({
    queryKey: ['campaign-burnup', selectedId],
    queryFn: async () => {
      const res = await pbAuthedFetch(`/api/campaigns/${selectedId}/burnup`);
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    enabled: !!selectedId,
  });

  const { data: ticketsData } = useQuery({
    queryKey: ['campaign-tickets', selectedId],
    queryFn: async () => {
      const res = await pbAuthedFetch(`/api/campaigns/${selectedId}/tickets`);
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    enabled: !!selectedId,
  });

  const detail = detailData;
  const burnup = burnupData?.series || [];
  const tickets = ticketsData;

  // Group campaigns by parent program
  const grouped: Record<string, any[]> = {};
  for (const c of campaigns) {
    const group = c.parent_program || 'Ungrouped';
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push(c);
  }

  const statusPill = (status: string) => {
    const cls =
      status === 'completed' ? 'pill--ok' :
      status === 'in_progress' ? 'pill--warn' :
      status === 'cancelled' ? 'pill--risk' :
      status === 'planned' || status === 'paused' ? 'pill--info' :
      'pill--info';
    return <span className={`pill ${cls}`}><i></i>{status.replace('_', ' ')}</span>;
  };

  const typePill = (type: string) => {
    return <span className="pill pill--info"><i></i>{type}</span>;
  };

  const statePill = (state: string) => {
    const cls =
      state === 'complete' ? 'pill--ok' :
      state === 'in_progress' ? 'pill--warn' :
      state === 'failed' ? 'pill--risk' :
      state === 'deferred' ? 'pill--warn' :
      'pill--info';
    return <span className={`pill ${cls}`}><i></i>{state?.replace('_', ' ') || '—'}</span>;
  };

  // Wave table columns
  const waveColumns: Column<any>[] = [
    { key: 'name', label: 'Scope Item', accessor: (r: any) => r.name || r.ci_name, render: (r: any) => <span className="td--name">{r.name || r.ci_name || '—'}</span> },
    { key: 'wave', label: 'Wave', render: (r: any) => <span className="td--id">{r.wave ?? '—'}</span> },
    { key: 'state', label: 'State', render: (r: any) => statePill(r.state) },
    { key: 'planned_start', label: 'Planned Start', render: (r: any) => <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>{r.planned_start || '—'}</span> },
    { key: 'actual_start', label: 'Actual Start', render: (r: any) => <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>{r.actual_start || '—'}</span> },
    { key: 'planned_end', label: 'Planned End', render: (r: any) => <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>{r.planned_end || '—'}</span> },
    { key: 'actual_end', label: 'Actual End', render: (r: any) => <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>{r.actual_end || '—'}</span> },
  ];
  const waves = detail?.waves || [];
  const waveCtrl = useTableControls(waves, waveColumns, { storageKey: 'campaign-waves' });

  // Loading state
  if (isLoading) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Portfolio</p>
              <h1 className="pagehead__title">Campaign Portfolio</h1>
              <div className="pagehead__facts">
                <span className="fact">ESS program view</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="page-loading" role="status" aria-live="polite">
                Loading campaign data…
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Portfolio</p>
              <h1 className="pagehead__title">Campaign Portfolio</h1>
              <div className="pagehead__facts">
                <span className="fact">ESS program view</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="state state--risk">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Failed to load campaign data</h3>
                <p className="state__body">
                  Could not connect to the campaigns API. Please check your connection and try again.
                </p>
                <button className="btn btn--primary" onClick={() => refetch()}>
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Portfolio</p>
            <h1 className="pagehead__title">Campaign Portfolio</h1>
            <div className="pagehead__facts">
              <span className="fact">ESS program view</span>
              <span className="fact">All migration campaigns, scope, velocity, and attributed impact</span>
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        {/* Completeness statement */}
        <div className="notice notice--info">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div>
            <p className="notice__title">Campaign Coverage</p>
            <p className="notice__body">
              <strong>{campaigns.length}</strong> campaign{campaigns.length !== 1 ? 's' : ''} tracked.
              {campaignsData?.total_scope != null && (
                <> Total scope: <strong>{campaignsData.total_scope.toLocaleString()}</strong> devices across all campaigns.</>
              )}
            </p>
          </div>
        </div>

        {/* Portfolio Index — grouped by parent program */}
        {Object.entries(grouped).map(([program, items]: [string, any[]]) => (
          <div key={program} className="box" style={{ marginBottom: 'var(--sp-4)' }}>
            <div className="box__head">
              <div className="box__head-text">
                <p className="box__eyebrow">Program</p>
                <h2 className="box__title">{program}</h2>
              </div>
            </div>
            <div className="box__body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 'var(--sp-4)' }}>
                {items.map((c: any) => {
                  const completed = c.completed ?? 0;
                  const total = c.total_scope ?? 0;
                  const pct = total > 0 ? ((completed / total) * 100).toFixed(0) : '0';
                  const isSelected = selectedId === c.id;
                  const healthPillCls =
                    c.health === 'green' ? 'pill--ok' :
                    c.health === 'amber' ? 'pill--warn' :
                    'pill--risk';

                  return (
                    <div
                      key={c.id}
                      className="box"
                      style={{
                        cursor: 'pointer',
                        borderColor: isSelected ? 'var(--gold)' : undefined,
                        borderWidth: isSelected ? '2px' : undefined,
                        transition: 'border-color 0.15s',
                      }}
                      onClick={() => setSelectedId(isSelected ? null : c.id)}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isSelected}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedId(isSelected ? null : c.id); } }}
                    >
                      <div className="box__head">
                        <div className="box__head-text">
                          <h3 className="box__title" style={{ fontSize: 'var(--text-lg)' }}>{c.name}</h3>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap', marginTop: 'var(--sp-1)' }}>
                            {typePill(c.type || 'migration')}
                            {statusPill(c.status || 'unknown')}
                            <span className={`pill ${healthPillCls}`}><i></i>{c.health || 'amber'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="box__body">
                        <div style={{ fontSize: 'var(--text-lg)', fontWeight: 600, fontFamily: 'var(--sans)', color: 'var(--ink)' }}>
                          {completed.toLocaleString()} of {total.toLocaleString()}
                          <span style={{ color: 'var(--ink-45)', fontWeight: 400, marginLeft: 'var(--sp-1)' }}>({pct}%)</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)', fontSize: 'var(--text-sm)' }}>
                          <div>
                            <span style={{ color: 'var(--ink-45)', fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Velocity</span>
                            <div style={{ fontFamily: 'var(--mono)', fontWeight: 500, marginTop: 'var(--sp-1)' }}>{c.velocity != null ? `${c.velocity} dev/wk` : '—'}</div>
                          </div>
                          <div>
                            <span style={{ color: 'var(--ink-45)', fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Completion</span>
                            <div style={{ fontFamily: 'var(--mono)', fontWeight: 500, marginTop: 'var(--sp-1)' }}>{c.projected_completion || '—'}</div>
                          </div>
                          <div>
                            <span style={{ color: 'var(--ink-45)', fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>CI</span>
                            <div style={{ fontFamily: 'var(--mono)', fontWeight: 500, marginTop: 'var(--sp-1)' }}>
                              {c.ci_lower && c.ci_upper ? `${c.ci_lower}–${c.ci_upper}` : '—'}
                            </div>
                          </div>
                          <div>
                            <span style={{ color: 'var(--ink-45)', fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Open Tickets</span>
                            <div style={{ fontFamily: 'var(--mono)', fontWeight: 500, marginTop: 'var(--sp-1)', color: c.attributed_open_tickets > 0 ? 'var(--risk)' : undefined }}>
                              {c.attributed_open_tickets ?? '—'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}

        {/* Empty state */}
        {campaigns.length === 0 && (
          <div className="box">
            <div className="box__body">
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">No campaigns reported yet</h3>
                <p className="state__body">
                  Campaign data will appear here once configured in the ESS program.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Campaign Detail */}
        {selectedId && detail && (
          <div className="box" style={{ borderTop: '3px solid var(--gold)' }}>
            <div className="box__head">
              <div className="box__head-text">
                <p className="box__eyebrow">Campaign Detail</p>
                <h2 className="box__title">{detail.name}</h2>
              </div>
              <div className="box__head-actions">
                <button className="btn btn--sm" onClick={() => setSelectedId(null)}>
                  Close
                </button>
              </div>
            </div>
            <div className="box__body">
              {/* Scope gap statement */}
              <div className="notice notice--info" style={{ marginBottom: 'var(--sp-4)' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                </svg>
                <div>
                  <p className="notice__title">Scope Gap</p>
                  <p className="notice__body">
                    <strong>{detail.total_scope?.toLocaleString() ?? '—'}</strong> in scope ·{' '}
                    <strong>{detail.matched_to_cis?.toLocaleString() ?? '—'}</strong> matched to CIs ·{' '}
                    <span style={{ color: 'var(--risk)' }}>
                      <strong>{detail.unmatched?.toLocaleString() ?? '—'}</strong> unmatched
                    </span>
                  </p>
                </div>
              </div>

              {/* Burn-up Chart */}
              <div style={{ marginBottom: 'var(--sp-5)' }}>
                <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, fontFamily: 'var(--serif)', fontStyle: 'italic', marginBottom: 'var(--sp-3)', color: 'var(--ink)' }}>Burn-Up — Completed vs Total Scope</h3>
                {burnup.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <ComposedChart data={burnup} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--hair)" />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill: 'var(--ink-45)' }} />
                      <YAxis tick={{ fontSize: 12, fill: 'var(--ink-45)' }} label={{ value: 'Devices', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: 'var(--ink-45)' } }} />
                      <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--hair)', borderRadius: 'var(--r-ctl)', fontSize: 'var(--text-sm)' }} />
                      <Legend wrapperStyle={{ fontSize: 'var(--text-sm)' }} />
                      <Area type="monotone" dataKey="ci_upper" name="Upper CI" stroke="transparent" fill="var(--gold)" fillOpacity={0.1} />
                      <Area type="monotone" dataKey="ci_lower" name="Lower CI" stroke="transparent" fill="var(--surface)" fillOpacity={1} />
                      <Line type="monotone" dataKey="completed" name="Completed" stroke="var(--ok)" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="total_scope" name="Total Scope" stroke="var(--navy)" strokeWidth={1.5} strokeDasharray="6 3" dot={false} />
                      <Line type="monotone" dataKey="projected" name="Projected" stroke="var(--gold)" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="state">
                    <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                    <h3 className="state__title">No burn-up data available</h3>
                    <p className="state__body">
                      Burn-up tracking data will appear once the campaign has started.
                    </p>
                  </div>
                )}
              </div>

              {/* Phase Timeline */}
              {detail.phases && detail.phases.length > 0 && (
                <div style={{ marginBottom: 'var(--sp-5)' }}>
                  <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, fontFamily: 'var(--serif)', fontStyle: 'italic', marginBottom: 'var(--sp-3)', color: 'var(--ink)' }}>Phase Timeline</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <div style={{ display: 'flex', gap: 'var(--sp-3)', minWidth: 'max-content', padding: 'var(--sp-2) 0' }}>
                      {detail.phases.map((phase: any, i: number) => {
                        const isBlocked = phase.status === 'blocked';
                        return (
                          <div key={i} className="box" style={{ minWidth: 140, borderColor: isBlocked ? 'var(--risk)' : undefined, background: isBlocked ? 'color-mix(in srgb, var(--risk) 4%, transparent)' : undefined }}>
                            <div className="box__body" style={{ padding: 'var(--sp-3)' }}>
                              <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)', marginBottom: 'var(--sp-1)', color: 'var(--ink)' }}>
                                {phase.name}
                                {isBlocked && <span style={{ color: 'var(--risk)', marginLeft: 'var(--sp-1)' }}>⚠</span>}
                              </div>
                              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)', fontFamily: 'var(--mono)' }}>
                                {phase.planned_start || '—'} → {phase.planned_end || '—'}
                              </div>
                              {phase.actual_start && (
                                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ok)', marginTop: 'var(--sp-1)' }}>
                                  Actual: {phase.actual_start} → {phase.actual_end || 'in progress'}
                                </div>
                              )}
                              <div style={{ marginTop: 'var(--sp-2)' }}>
                                {statePill(phase.status)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Wave Table */}
              <div style={{ marginBottom: 'var(--sp-5)' }}>
                <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, fontFamily: 'var(--serif)', fontStyle: 'italic', marginBottom: 'var(--sp-3)', color: 'var(--ink)' }}>Scope Items by Wave</h3>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)', display: 'block', marginBottom: 'var(--sp-2)' }}>{waves.length} items</span>
                <DataTable
                  columns={waveColumns}
                  visibleColumns={waveCtrl.visibleColumns}
                  data={waveCtrl.paged}
                  search={waveCtrl.search}
                  onSearchChange={waveCtrl.setSearch}
                  sortKey={waveCtrl.sortKey}
                  sortDir={waveCtrl.sortDir}
                  onSort={waveCtrl.setSort}
                  page={waveCtrl.page}
                  totalPages={waveCtrl.totalPages}
                  totalFiltered={waveCtrl.totalFiltered}
                  pageSize={waveCtrl.pageSize}
                  onPageChange={waveCtrl.setPage}
                  onPageSizeChange={waveCtrl.setPageSize}
                  activeFilters={waveCtrl.activeFilters}
                  onFilter={waveCtrl.setFilter}
                  onClearFilters={waveCtrl.clearFilters}
                  searchPlaceholder="Search scope items..."
                  keyAccessor={(r: any) => r.id || r.name}
                  emptyMessage="No scope items reported."
                  hiddenColumns={waveCtrl.hiddenColumns}
                  onToggleColumn={waveCtrl.toggleColumn}
                  selectedIds={waveCtrl.selectedIds}
                  onToggleSelect={waveCtrl.toggleSelect}
                  onSelectAll={waveCtrl.selectAll}
                  onClearSelection={waveCtrl.clearSelection}
                  savedViews={waveCtrl.savedViews}
                  onSaveView={waveCtrl.saveView}
                  onLoadView={waveCtrl.loadView}
                  onDeleteView={waveCtrl.deleteView}
                  timeRange={waveCtrl.timeRange}
                  onTimeRangeChange={waveCtrl.setTimeRange}
                  onExportCSV={waveCtrl.exportCSV}
                  onExportExcel={waveCtrl.exportExcel}
                />
              </div>

              {/* Attributed Tickets Panel */}
              <div>
                <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, fontFamily: 'var(--serif)', fontStyle: 'italic', marginBottom: 'var(--sp-3)', color: 'var(--ink)' }}>Attributed Tickets</h3>
                {tickets ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--sp-4)' }}>
                    {(tickets.by_basis || []).map((b: any) => (
                      <div className="box" key={b.attribution_basis}>
                        <div className="box__head">
                          <div className="box__head-text">
                            <p className="box__eyebrow">Attribution</p>
                            <h3 className="box__title" style={{ fontSize: 'var(--text-base)' }}>{b.attribution_basis}</h3>
                          </div>
                        </div>
                        <div className="box__body">
                          <div style={{ display: 'flex', gap: 'var(--sp-5)' }}>
                            <div>
                              <span style={{ color: 'var(--ink-45)', fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Open</span>
                              <div style={{ fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 'var(--text-lg)', marginTop: 'var(--sp-1)', color: b.open > 0 ? 'var(--risk)' : undefined }}>
                                {b.open ?? 0}
                              </div>
                            </div>
                            <div>
                              <span style={{ color: 'var(--ink-45)', fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Closed</span>
                              <div style={{ fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 'var(--text-lg)', marginTop: 'var(--sp-1)' }}>
                                {b.closed ?? 0}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {tickets.unattributed != null && (
                      <div className="box" style={{ borderColor: 'var(--gold-soft)' }}>
                        <div className="box__head">
                          <div className="box__head-text">
                            <p className="box__eyebrow">Unattributed</p>
                          </div>
                        </div>
                        <div className="box__body">
                          <div style={{ fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 'var(--text-2xl)', color: 'var(--gold)' }}>
                            {tickets.unattributed}
                          </div>
                          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)', marginTop: 'var(--sp-1)' }}>
                            Tickets not matched to any attribution basis
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="state">
                    <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <h3 className="state__title">No ticket attribution data</h3>
                    <p className="state__body">
                      Ticket attribution will appear once tickets are linked to campaign scope items.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
